import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  Input,
  Label,
} from "reactstrap";
import Breadcrumbs from "../../../../CommonElements/Breadcrumbs/Breadcrumbs";
import { useEffect, useState } from "react";
import apiRequestHelper from "../../../../utils/apiRequestHelper";
import { toast } from "react-toastify";
import { Btn } from "../../../../AbstractElements";

interface CommonFormType {
  id: number;
  email: string;
  name: string;
  contact: string;
  city?: string | null;
  service?: string | null;
  companyUrl?: string | null;
  page: string;
}

interface PaginationType {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

const CommonFormsContainer = () => {
  const [forms, setForms] = useState<CommonFormType[]>([]);
  const [loading, setLoading] = useState(true);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [pagination, setPagination] = useState<PaginationType>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });

  useEffect(() => {
    fetchCommonForms();
  }, [page, limit, search]);

  const fetchCommonForms = async () => {
    try {
      setLoading(true);

      const response = await apiRequestHelper.get("/common-form", {
        params: {
          page,
          limit,
          search,
        },
      });

      setForms(response.data?.data?.forms || []);
      setPagination(
        response.data?.data?.pagination || {
          page: 1,
          limit,
          total: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPrevPage: false,
        }
      );
    } catch (error: any) {
      toast.error(
        error.response?.data?.messages?.[0]?.message ||
        "Failed to load common forms"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleReset = () => {
    setSearchInput("");
    setSearch("");
    setPage(1);
    setLimit(10);
  };

  return (
    <>
      <Breadcrumbs mainTitle={"Common Forms"} parent={"Forms"} />

      <Container fluid>
        <Row className="mb-3">
          <Col md="6" className="mb-2">
            <Label>Search</Label>
            <div className="d-flex gap-2">
              <Input
                type="text"
                value={searchInput}
                placeholder="Search by name, email, contact, city, service..."
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
              />
              <Btn color="primary" onClick={handleSearch}>
                Search
              </Btn>
              <Btn color="secondary" onClick={handleReset}>
                Reset
              </Btn>
            </div>
          </Col>

          <Col md="3" className="mb-2">
            <Label>Per Page</Label>
            <Input
              type="select"
              value={limit}
              onChange={(e) => {
                setPage(1);
                setLimit(Number(e.target.value));
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Input>
          </Col>

          <Col
            md="3"
            className="mb-2 d-flex align-items-end justify-content-md-end"
          >
            <div className="text-white">
              Total Records: <strong>{pagination.total}</strong>
            </div>
          </Col>
        </Row>

        <Row>
          <Col xs="12">
            <Card>
              <CardBody>
                {loading ? (
                  <div className="text-center">
                    <p className="mb-0">Loading forms...</p>
                  </div>
                ) : forms.length === 0 ? (
                  <div className="text-center">
                    <p className="mb-0">No forms found.</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table
                        bordered
                        hover
                        responsive
                        className="align-middle mb-0"
                      >
                        <thead>
                          <tr>
                            <th className="text-white" style={{ width: "70px" }}>#</th>
                            <th className="text-white">Name</th>
                            <th className="text-white">Email</th>
                            <th className="text-white">Contact</th>
                            <th className="text-white">City</th>
                            <th className="text-white">Service</th>
                            <th className="text-white text-nowrap">Company URL</th>
                            <th className="text-white">Page</th>
                          </tr>
                        </thead>

                        <tbody>
                          {forms.map((item, index) => (
                            <tr key={item.id}>
                              <td className="text-white">{(page - 1) * limit + index + 1}</td>
                              <td className="text-white">{item.name || "N/A"}</td>
                              <td className="text-white">{item.email || "N/A"}</td>
                              <td className="text-white">{item.contact || "N/A"}</td>
                              <td className="text-white">{item.city || "N/A"}</td>
                              <td className="text-white">{item.service || "N/A"}</td>
                              <td className="text-white">
                                {item.companyUrl ? (
                                  <a
                                    href={item.companyUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {item.companyUrl}
                                  </a>
                                ) : (
                                  "N/A"
                                )}
                              </td>
                              <td className="text-white">{item.page || "N/A"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                      <div className="text-muted">
                        Page <strong>{pagination.page}</strong> of{" "}
                        <strong>{pagination.totalPages || 1}</strong>
                      </div>

                      <div className="d-flex gap-2">
                        <Btn
                          color="secondary"
                          disabled={!pagination.hasPrevPage}
                          onClick={() => setPage((prev) => prev - 1)}
                        >
                          Previous
                        </Btn>

                        <Btn
                          color="primary"
                          disabled={!pagination.hasNextPage}
                          onClick={() => setPage((prev) => prev + 1)}
                        >
                          Next
                        </Btn>
                      </div>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default CommonFormsContainer;